<script setup lang="ts">
import api from "@/api";
import { useSessionStore } from "@/store/session";
import type { TTeacherClassRequestsParams } from "@/api/types";
import type {
	TTeacherClassRequest,
	TPagination,
	TClassesFilters,
} from "@/assets/js/globalTypes";
import {
	openClassRequestModal,
	openConfirmDialog,
	openTeacherReviewClassRequestModal,
} from "@/components/modals/modalDefinitions";
import type { PageState } from "primevue/paginator";
import {
	formatPagination,
	updatePagination,
	getRequestPagination,
	useIsMobile,
	setupChangesFields,
} from "@/assets/js/helpers";
import { useToastStore } from "@/store/toast";
import { $t } from "@/i18n";
import type Popover from "primevue/popover";
import { DateInstance, SimplifiedDate } from "@/assets/js/dateHelper";
import { useTeacherStore } from "@/store/teacher";
import dayjs from "dayjs";
import { useCalendarStore } from "@/store/calendar";

defineOptions({
	name: "teacher.requests",
});
definePage({
	name: "teacher.requests",
});

type TClassesFiltersParams = {
	date_from: string | null;
	date_to: string | null;
	scl_ids: string;
	cit_ids: string;
};

const op2 = ref<InstanceType<typeof Popover> | null>(null);
const toastStore = useToastStore();
const teacherStore = useTeacherStore();
const sessionStore = useSessionStore();
const route = useRoute();
const router = useRouter();
const activeTab = ref<TTeacherClassRequestsParams["crq_status"]>("OPEN");
const isLoading = ref(false);
const clsRequests = ref<TTeacherClassRequest[] | null>(null);
const selectedClsRequest = ref<TTeacherClassRequest | null>(null);
const isMobile = useIsMobile();
const rows = ref(6); // Don't change this
const preserveQuery = ref(false);
const isVisibleFiltersPanel = ref(false);
const tabs = computed(() => [
	{ label: $t("app.school.classManagement.status.pending"), value: "OPEN" },

	{
		label: $t("app.school.classManagement.status.accepted"),
		value: "ACCEPTED",
	},

	{
		label: $t("app.school.classManagement.status.rejected"),
		value: "REJECTED",
	},
]);

const clsRequestsPagination = ref<TPagination | null>(null);
const cities = ref<{ cit_id: number; cit_name: string }[]>([]);
const schools = ref<{ scl_id: number; scl_name: string }[]>([]);
const selectedClassesFiltersChanges = ref<TClassesFilters>({
	cities: [],
	schools: [],
	from: null,
	to: null,
});
provide("selectedClassesFiltersChanges", selectedClassesFiltersChanges);
const selectedClassesFilters = ref<TClassesFilters>({
	cities: [],
	schools: [],
	from: null,
	to: null,
});
const classesFilterKey = ref(1);
const filterChips = ref<{ key: string; value: string }[]>(null);
const showRequests = ref(false);
const calendarStore = useCalendarStore();

async function getClassRequests(
	status: TTeacherClassRequestsParams["crq_status"],
	pagination?: PageState,
) {
	isLoading.value = true;
	if (pagination) {
		const updatedPagination = formatPagination(pagination);
		updatePagination(clsRequestsPagination, updatedPagination);
	}
	// Prepare the request parameters
	const processedParams: TClassesFiltersParams = preprocessFilterParams(
		selectedClassesFilters.value,
	);
	try {
		const res = await api.getClassRequests({
			usr_id: sessionStore.userId,
			crq_status: status,
			...processedParams,
			...getRequestPagination(clsRequestsPagination.value, rows.value),
		});
		clsRequests.value = res.data.data;
		clsRequestsPagination.value = res.data.meta?.pagination || null;
		const pattern = /(crr_sent|\bfrom\b|\bto\b)/i;
		calendarStore.adjustTimeForUnknownObject(pattern, clsRequests.value);

		await getClassesFilters();
	} catch (error) {
		console.error(error);
	} finally {
		isLoading.value = false;
	}
}

function setActiveTabFromQuery() {
	const tabFromQuery = route.query.status;
	if (tabFromQuery && typeof tabFromQuery === "string") {
		const upperStatus = tabFromQuery.toUpperCase();
		activeTab.value = ["OPEN", "ACCEPTED", "REJECTED", "CANCELED"].includes(
			upperStatus as any,
		)
			? (upperStatus as TTeacherClassRequestsParams["crq_status"])
			: "OPEN";
	}
}

async function setActiveTab(status: string) {
	activeTab.value = ["OPEN", "ACCEPTED", "REJECTED", "CANCELED"].includes(
		status,
	)
		? (status as TTeacherClassRequestsParams["crq_status"])
		: "OPEN";

	router.push({
		name: "teacher.requests",
		query: {
			...route.query,
			status: activeTab.value.toLowerCase(),
		},
	});
}

async function findSelectedRequest(crq_id?: number) {
	if (route.query.id || crq_id) {
		const id = ref<string | number>(-1);

		if (crq_id) {
			id.value = crq_id;
		} else {
			id.value = Array.isArray(route.query.id)
				? route.query.id[0]
				: route.query.id;
		}

		const numericId = Number(id.value);

		try {
			const res = await api.getTeacherClassRequest(numericId);
			const originalRequest = res.data.data;

			if (originalRequest) {
				const pattern =
					/(start|end|date|deleted|created|crr_sent|\bfrom\b|\bto\b)/i;
				calendarStore.adjustTimeForUnknownObject(pattern, originalRequest);

				if (originalRequest.crq_status !== activeTab.value) {
					activeTab.value = originalRequest.crq_status;
					await getClassRequests(activeTab.value);
				}

				selectedClsRequest.value = originalRequest;
			}
		} catch (error) {
			toastStore.openToastInfo(
				$t("app.base.no-results", {
					item: $t("app.teacher.classRequests.classRequest"),
				}),
			);
			clearSelectedClass();
			console.error(error);
		}
	} else {
		selectedClsRequest.value = null;
	}
}

watch(
	() => route.fullPath,
	async () => {
		showRequests.value = false;
		setActiveTabFromQuery();
		findSelectedRequest();
		await getClassRequests(activeTab.value);
		nextTick(() => {
			showRequests.value = true;
		});
	},
);

async function onChangeTab(newValue: any) {
	showRequests.value = false;
	setActiveTab(newValue as TTeacherClassRequestsParams["crq_status"]);
	clsRequestsPagination.value.current_page = 1;
	await getClassRequests(activeTab.value);
	nextTick(() => {
		showRequests.value = true;
	});
}

onMounted(async () => {
	showRequests.value = false;
	setActiveTabFromQuery();
	findSelectedRequest();
	await getClassRequests(activeTab.value);
	nextTick(() => {
		showRequests.value = true;
	});
});

function onSwitchToMobile() {
	preserveQuery.value = true;
}

function onCloseModal() {
	if (preserveQuery.value) {
		return;
	} else {
		clearSelectedClass();
	}
}

function clearSelectedClass() {
	selectedClsRequest.value = null;

	const newQuery = { ...route.query };
	delete newQuery.id;

	router.replace({
		name: "teacher.requests",
		query: newQuery,
	});
}

watch(selectedClsRequest, (newValue) => {
	if (newValue && isMobile.value) {
		document.body.classList.add("no-scroll-y");
	} else {
		document.body.classList.remove("no-scroll-y");
	}

	if (newValue && !isMobile.value) {
		openClassRequestModal(
			selectedClsRequest.value,
			onCloseModal,
			onSwitchToMobile,
			handleReviewClass,
		);
		preserveQuery.value = false;
	}
});

watch(
	() => teacherStore.classRequestsKey,
	() => {
		findSelectedRequest();
		getClassRequests(activeTab.value);
	},
);

onBeforeUnmount(() => {
	document.body.classList.remove("no-scroll-y");
});

watch(
	() => isMobile.value,
	(newValue) => {
		if (!newValue) {
			document.body.classList.remove("no-scroll-y");

			if (selectedClsRequest.value) {
				openClassRequestModal(
					selectedClsRequest.value,
					onCloseModal,
					onSwitchToMobile,
				);
				preserveQuery.value = false;
			}
		}
	},
);

// *****

async function rejectClassRequest(classRequest: TTeacherClassRequest) {
	try {
		const res = await api.rejectClassRequest({ crq_id: classRequest.crq_id });
		toastStore.openToastSuccess(res.data.message);
	} catch (error) {
		toastStore.openToastError(error.response?.data?.message || error.message);
	} finally {
		getClassRequests(activeTab.value);
	}
}
async function acceptClassRequest(classRequest: TTeacherClassRequest) {
	try {
		const res = await api.acceptClassRequest(classRequest.crq_id);
		toastStore.openToastSuccess(res.data.message);
	} catch (error) {
		toastStore.openToastError(error.response?.data?.message || error.message);
	} finally {
		getClassRequests(activeTab.value);
	}
}

function handleReviewClass(classRequest: TTeacherClassRequest) {
	openTeacherReviewClassRequestModal({
		classRequest,
		initialDate: dayjs(classRequest.from),
		onReject: rejectClassRequest,
		onAccept: (_t, file_access) => {
			if (file_access) {
				acceptClassRequest(classRequest);
				return;
			}
			openConfirmDialog(
				$t("app.base.confirm"),
				$t("app.booking.fileShareText"),
				() => acceptClassRequest(classRequest),
			);
		},
	});
}

// ***** FILTERS

// Toggles the filter panel visibility
const toggle = (event) => {
	op2.value.toggle(event);
};

function resetFilters() {
	selectedClassesFiltersChanges.value = {
		cities: [],
		schools: [],
		from: null,
		to: null,
	};

	selectedClassesFilters.value = {
		cities: [],
		schools: [],
		from: null,
		to: null,
	};

	filterChips.value = null;
	teacherStore.filtersSelectedDateOption = "";

	classesFilterKey.value += 1;

	getClassRequests(activeTab.value);
}

function removeFilter(filterToRemove: { key: string; value: string }) {
	switch (filterToRemove.key) {
		case "city": {
			selectedClassesFiltersChanges.value.cities =
				selectedClassesFiltersChanges.value.cities.filter(
					(c) => c.cit_name !== filterToRemove.value,
				);
			break;
		}
		case "school": {
			selectedClassesFiltersChanges.value.schools =
				selectedClassesFiltersChanges.value.schools.filter(
					(s) => s.scl_name !== filterToRemove.value,
				);
			break;
		}
		case "from": {
			selectedClassesFiltersChanges.value.from = null;
			break;
		}
		case "to": {
			selectedClassesFiltersChanges.value.to = null;
			break;
		}
	}

	applyFilters();
}

function createFilterChips() {
	const chips: { key: string; value: string }[] = [];

	if (selectedClassesFilters.value.cities.length > 0) {
		for (const city of selectedClassesFilters.value.cities) {
			chips.push({
				key: "city",
				value: city.cit_name,
			});
		}
	}

	if (selectedClassesFilters.value.schools.length > 0) {
		for (const school of selectedClassesFilters.value.schools) {
			chips.push({
				key: "school",
				value: school.scl_name,
			});
		}
	}

	if (selectedClassesFilters.value.from) {
		chips.push({
			key: "from",
			value:
				"From: " +
				DateInstance.getFormattedDate(
					selectedClassesFilters.value.from,
					"YYYY-MM-DD",
					false,
				),
		});
	}

	if (selectedClassesFilters.value.to) {
		chips.push({
			key: "to",
			value:
				"To: " +
				DateInstance.getFormattedDate(
					selectedClassesFilters.value.to,
					"YYYY-MM-DD",
					false,
				),
		});
	}

	filterChips.value = chips;
}

function applyFilters(event?: MouseEvent) {
	setupChangesFields(
		selectedClassesFiltersChanges.value,
		selectedClassesFilters,
	);
	createFilterChips();
	isVisibleFiltersPanel.value = false;
	if (event && !isMobile.value) toggle(event);
	clsRequestsPagination.value = null;
	getClassRequests(activeTab.value);
}

async function getClassesFilters() {
	isLoading.value = true;

	try {
		const res = await api.getCalendarFilters({
			usr_id: sessionStore.userId,
		});

		cities.value = res.data.data?.cities;
		schools.value = res.data.data?.schools;
	} catch (error) {
		console.error(error);
	} finally {
		isLoading.value = false;
	}
}

const preprocessFilterParams = (params: TClassesFilters) => {
	return {
		date_from: params.from
			? SimplifiedDate.fromLocalToUtc(params.from).format("YYYY-MM-DD")
			: null,
		date_to: params.to
			? SimplifiedDate.fromLocalToUtc(params.to).format("YYYY-MM-DD")
			: null,
		scl_ids: params.schools.map((s) => s.scl_id).join(",") || "",
		cit_ids: params.cities.map((c) => c.cit_id).join(",") || "",
	};
};
</script>

<template>
	<div id="classes-page">
		<div class="tabs-wrapper">
			<p-tabs
				:value="activeTab"
				:pt="{
					root: {
						class: 'tabs-inner-wrapper',
					},
				}"
				@update:value="(val) => onChangeTab(val)"
			>
				<p-tab-list>
					<p-tab
						v-for="tab in tabs"
						:key="tab.label"
						:value="tab.value"
						:pt="{
							root: {
								class: 'classes-page-tab',
							},
						}"
					>
						{{ tab.label }}
					</p-tab>
				</p-tab-list>
			</p-tabs>
			<p-button
				severity="contrast"
				rounded
				variant="outlined"
				:pt="{
					root: {
						class: 'filters-btn-root',
					},
				}"
				@click="toggle"
			>
				{{ $t("app.school.teacherSearch.filters") }}
				<i-ic-baseline-keyboard-arrow-down />
			</p-button>
		</div>
		<p-button
			severity="contrast"
			rounded
			variant="outlined"
			:pt="{
				root: {
					class: 'filters-btn-root-sm',
				},
			}"
			@click="isVisibleFiltersPanel = true"
		>
			{{ $t("app.school.teacherSearch.filters") }}
			<i-ic-baseline-keyboard-arrow-down />
		</p-button>
		<div
			class="chips-wrapper"
			:class="{
				'is-visible': filterChips && filterChips.length > 0,
			}"
			style="padding: 0 16px"
		>
			<span
				v-for="(filter, index) in filterChips"
				:key="index"
				class="chip-item"
			>
				<span class="chip-title">{{ filter.value }}</span>
				<span
					class="chip-btn"
					@click="removeFilter(filter)"
				>
					<i-uil-times-circle />
				</span>
			</span>
			<p-button
				v-if="filterChips && filterChips.length > 0"
				severity="info"
				variant="text"
				:pt="{
					root: {
						class: ['btn-root', 'btn-root-reset'],
					},
				}"
				@click="resetFilters()"
			>
				<i-material-symbols-forward-media-rounded />
				{{
					$t(
						"app.school.teacherSearch.filtersButton",
						{
							action: $t("app.base.reset"),
						},
						2,
					)
				}}
			</p-button>
		</div>
		<transition name="expand">
			<div v-if="showRequests">
				<class-requests
					v-if="clsRequests"
					:pagination="clsRequestsPagination"
					:class-requests="clsRequests"
					:is-loading="isLoading"
					:rows="rows"
					@update-rows="(e) => (rows = e)"
					@change-page="(e) => getClassRequests(activeTab, e)"
					@request-click="(e) => findSelectedRequest(e)"
				></class-requests>
			</div>
		</transition>
		<transition name="slide-fade">
			<div
				v-if="selectedClsRequest"
				class="class-request-outer-wrapper"
			>
				<div>
					<p-button
						as="router-link"
						variant="link"
						:to="{ name: 'teacher.requests' }"
						:pt="{
							root: {
								class: 'back-btn',
							},
						}"
					>
						<i-ic-baseline-arrow-back-ios />
						{{ $t("app.teacher.classRequests.backToRequests") }}
					</p-button>
				</div>

				<div class="title">{{ selectedClsRequest.scl_name }}</div>
				<class-request
					v-if="isMobile"
					:cls-request="selectedClsRequest"
					@review-class="handleReviewClass"
				></class-request>
			</div>
		</transition>
		<p-drawer
			v-if="isMobile"
			:visible="isVisibleFiltersPanel"
			:dismissable="true"
			:modal="true"
			position="left"
			:pt="{
				root: {
					class: 'filters-drawer-root',
				},
				header: {
					class: 'filters-drawer-header',
				},
				content: {
					class: 'filters-drawer-content',
				},
				footer: {
					class: 'filters-drawer-footer',
				},
			}"
			@update:visible="isVisibleFiltersPanel = !isVisibleFiltersPanel"
		>
			<template #header>
				<div class="header-content-wrapper">
					{{ $t("app.school.teacherSearch.filters") }}
				</div>
			</template>
			<classes-filters
				:key="classesFilterKey"
				:cities="cities"
				:schools="schools"
			/>
			<template #footer>
				<div class="btn-wrapper">
					<p-button
						outlined
						:pt="{
							root: {
								class: ['btn-root', 'btn-root-reset'],
							},
						}"
						@click="resetFilters()"
					>
						<i-material-symbols-forward-media-rounded />
						{{
							$t("app.school.teacherSearch.filtersButton", {
								action: $t("app.base.reset"),
							})
						}}
					</p-button>
					<p-button
						:pt="{
							root: {
								class: ['btn-root', 'btn-root-apply'],
							},
							label: {
								class: ['btn-label', 'btn-label-apply'],
							},
						}"
						@click="applyFilters()"
					>
						{{
							$t("app.school.teacherSearch.filtersButton", {
								action: $t("app.base.apply"),
							})
						}}
					</p-button>
				</div>
			</template>
		</p-drawer>
		<p-popover
			v-else
			ref="op2"
			:pt="{
				root: {
					class: 'filters-popover-root',
				},
				header: {
					class: 'teacher-search-popover-header',
				},
				content: {
					class: 'filters-popover-content',
				},
			}"
		>
			<div class="btn-wapper">
				<p-button
					:pt="{
						root: {
							class: ['btn-root', 'btn-root-apply'],
						},
						label: {
							class: ['btn-label', 'btn-label-apply'],
						},
					}"
					@click="(e) => applyFilters(e)"
				>
					{{
						$t("app.school.teacherSearch.filtersButton", {
							action: $t("app.base.apply"),
						})
					}}
				</p-button>
				<p-button
					outlined
					:pt="{
						root: {
							class: ['btn-root', 'btn-root-reset'],
						},
					}"
					@click="resetFilters()"
				>
					<i-material-symbols-forward-media-rounded />
					{{
						$t("app.school.teacherSearch.filtersButton", {
							action: $t("app.base.reset"),
						})
					}}
				</p-button>
			</div>
			<classes-filters
				:key="classesFilterKey"
				:cities="cities ?? []"
				:schools="schools ?? []"
			/>
		</p-popover>
	</div>
</template>

<style lang="scss" scoped>
$tab-height: 41px;
$classes-page-padding-sm: 16px;
$classes-page-padding: 20px;
$border-color: #6e6e8b;

#classes-page {
	box-sizing: border-box;
	padding: $classes-page-padding-sm 0 0;
	height: calc(100vh - $nav-top-bar - 1px);
	max-height: calc(100vh - $nav-top-bar - 1px);
	overflow-y: auto;

	.filters-btn-root-sm {
		border-color: $border-color;
		color: $border-color;
		font-size: 18px;
		font-family: Archivo, sans-serif;
		min-width: 111px;
		margin-top: 13px;
		margin-left: 16px;
	}

	.tabs-wrapper {
		.filters-btn-root {
			display: none;
		}

		.classes-page-tab {
			flex: 1;
			background-color: transparent;
			color: var(--p-primary-700);
			font-size: 16px;
			font-weight: 500;
			border: none;
			border-bottom: 2px solid transparent;
			padding: 0;
			height: 40px;
			display: flex;
			flex-wrap: nowrap;
			align-items: center;
			justify-content: center;
			transition: border-bottom 0.3s ease;

			.tab-badge {
				color: var(--p-primary-700);
				margin-left: 10px;
			}

			&.p-tab-active {
				color: $daidai-dark-blue;
				border-bottom: 2px solid var(--p-primary-600);
			}

			&:hover {
				color: $daidai-dark-blue;
				border-bottom: 2px solid
					color-mix(in srgb, var(--p-primary-600) 50%, transparent);
			}
		}
	}

	.class-request-outer-wrapper {
		position: fixed;
		top: $nav-top-bar;
		left: 0;
		bottom: 0;
		right: 0;
		background-color: $background-color;
		display: flex;
		flex-direction: column;
		padding: 10px 16px 30px;
		overflow-y: auto;

		.back-btn {
			width: 144px;
			height: 17px;
			padding: 0;
			margin-bottom: 24px;
			font-size: 16px;
			font-weight: 600;
			font-family: Archivo, sans-serif;
			white-space: nowrap;
		}

		.title {
			font-size: 18px;
			font-weight: 700;
			font-family: Archivo, sans-serif;
			color: $daidai-dark-blue;
			padding: 0 0 17px;
		}
	}

	@media screen and (width >= 768px) {
		padding: 20px 0 0;
		min-width: 575px;
		height: calc(100vh - $nav-top-bar - 1px);
		max-height: calc(100vh - $nav-top-bar - 1px);
		overflow-y: auto;

		.filters-btn-root-sm {
			display: none;
		}

		.tabs-wrapper {
			display: flex;

			.filters-btn-root {
				display: flex;
				border-color: $border-color;
				color: $border-color;
				font-size: 18px;
				font-family: Archivo, sans-serif;
				min-width: 111px;
				margin-right: 20px;
			}

			.tabs-inner-wrapper {
				padding: 0 37px;
				flex: 1;
			}

			.classes-page-tab {
				font-size: 18px;
				height: $tab-height;
			}
		}

		.content-wrapper {
			max-height: calc(
				100vh - $nav-top-bar - $tab-height - $classes-page-padding * 2 - 1px
			);
		}

		.class-request-outer-wrapper {
			display: none;
		}
	}

	.slide-fade-enter-active,
	.slide-fade-leave-active {
		transition: all 0.3s ease;
	}

	.slide-fade-enter,
	.slide-fade-enter-from,
	.slide-fade-leave-to {
		transform: translateY(10px);
		opacity: 0;
	}
}
</style>
